import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { useDream } from '@/hooks/useDream'
import { useItem } from '@/hooks/useItem'
import { useProUpgrade } from '@/hooks/useProUpgrade'
import { useLanguage } from '@/hooks/useLanguage'
import { useLoading } from '@/store/contexts/LoadingContext'
import { t } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import { DreamityItemResDto, DreamityItemSaveReqDto } from '@/store/types/checklist.types'
import Card from '@/components/common/Card/Card'
import Button from '@/components/common/Button/Button'
import Modal from '@/components/common/Modal/Modal'
import { Input } from '@/components/common/Input/Input'
import { useToastContext } from '@/store/contexts/ToastContext'
import Skeleton, { SkeletonListItem } from '@/components/common/Skeleton/Skeleton'
import EmptyState from '@/components/common/EmptyState/EmptyState'
import PageHeader from '@/components/common/PageHeader/PageHeader'

import { IconPackage, IconEdit, IconTrash, IconClose, IconPlus } from '@/components/icons/Icons'
import './Checklist.css'

export interface ChecklistProps {
  dreamIdProp?: string
  planIdProp?: string
  goalIdProp?: string
  isModal?: boolean
  onClose?: () => void
  /** 아이템 생성/수정/삭제 후 계획 목록(예: 아이템 예산) 갱신을 위해 부모에서 호출할 콜백 */
  onItemUpdated?: () => void
}

function Checklist(props: ChecklistProps = {}) {
  const { dreamIdProp, planIdProp, goalIdProp, isModal = false, onClose, onItemUpdated } = props
  const { id, planId } = useParams<{ id: string; planId: string }>()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const {
    items: allItems,
    loading: itemLoading,
    fetchItems,
    fetchItemById,
    createItem,
    updateItem,
    deleteItem,
  } = useItem()

  const currentDreamId = dreamIdProp ?? id ?? null
  const currentPlanId = planIdProp ?? planId ?? null

  // Filter items for current plan
  const items = allItems.filter(item => String(item.planId) === String(currentPlanId))

  const { handleRestrictionError } = useProUpgrade()
  const { selectedDream } = useDream()
  const { startLoading, stopLoading } = useLoading()
  const { showToast } = useToastContext()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<DreamityItemResDto | null>(null)
  const [editForm, setEditForm] = useState<DreamityItemResDto | null>(null)
  const [isCreatingItem, setIsCreatingItem] = useState(false)
  const [isUpdatingItem, setIsUpdatingItem] = useState(false)
  const [isDeletingItem, setIsDeletingItem] = useState(false)

  const [createForm, setCreateForm] = useState<DreamityItemSaveReqDto>({
    planId: '',
    dreamId: '',
    goalId: '',
    itemName: '',
    itemDesc: '',
    itemLink: '',
    itemPrice: null,
    isCompleted: false,
  })
  const hasFetchedRef = useRef<string | null>(null)
  /** 모달로 열렸을 때, 로딩이 한 번이라도 true였다가 false가 된 뒤에만 stopLoading 호출 (초기 마운트에서 즉시 stopLoading 방지) */
  const itemLoadingWasTrueRef = useRef(false)
  /** 아이템 수정 모달을 위해 fetchItemById 호출 중일 때 true. 이때는 itemLoading이 true여도 전체 스켈레톤을 보이지 않음 */
  const isFetchingItemForEditRef = useRef(false)

  useEffect(() => {
    if (!currentPlanId) return
    // 같은 planId에 대해 이미 호출했으면 중복 호출 방지
    if (hasFetchedRef.current === currentPlanId) {
      return
    }
    
    hasFetchedRef.current = currentPlanId
    // call fetchItems with dreamId + planId to match backend API
    fetchItems(currentDreamId!, currentPlanId).catch(() => {
      // 에러 발생 시 리셋하여 재시도 가능하도록
      hasFetchedRef.current = null
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlanId, currentDreamId])

  useEffect(() => {
    if (!currentPlanId || !currentDreamId) return
    setCreateForm((prev) => ({
      ...prev,
      planId: currentPlanId,
      dreamId: currentDreamId,
      goalId: goalIdProp ?? prev.goalId,
    }))
  }, [currentPlanId, currentDreamId, goalIdProp])

  if (itemLoading) itemLoadingWasTrueRef.current = true

  // 메뉴 이동 / 아이템 모달: 아이템 목록 로딩 완료 후 공통 오버레이 종료
  useEffect(() => {
    if (currentPlanId == null || currentDreamId == null) return
    if (!itemLoading) {
      if (!isModal) {
        stopLoading()
      } else {
        if (itemLoadingWasTrueRef.current) stopLoading()
      }
    }
  }, [currentPlanId, currentDreamId, itemLoading, stopLoading, isModal])

  const handleCreateItem = async () => {
    if (!currentPlanId) return

    // 폼 검증
    if (!createForm.itemName.trim()) {
      showToast(t('checklist.itemNameRequired', language) || '아이템 이름을 입력해주세요.', 'error')
      return
    }
    const gid = (goalIdProp || createForm.goalId || '').trim()
    if (!gid) {
      showToast(
        t('checklist.goalRequired', language) || '목표 정보가 없어 아이템을 만들 수 없습니다.',
        'error'
      )
      return
    }

    if (createForm.itemPrice !== null && createForm.itemPrice < 0) {
      showToast(t('messages.invalidBudget', language) || '가격은 0 이상이어야 합니다.', 'error')
      return
    }

    try {
      setIsCreatingItem(true)
      startLoading()
      // pass dreamId to createItem → planner-data 세그먼트 POST .../item/{dreamId}
      await createItem(currentDreamId!, {
        ...createForm,
        planId: currentPlanId!,
        dreamId: currentDreamId!,
        goalId: gid,
        itemName: createForm.itemName.trim(),
        itemDesc: createForm.itemDesc.trim(),
        itemLink: createForm.itemLink.trim(),
        itemPrice: createForm.itemPrice ?? 0,
        isCompleted: createForm.isCompleted,
      })
      setIsCreateModalOpen(false)
      setCreateForm({
        planId: currentPlanId!,
        dreamId: currentDreamId!,
        goalId: gid,
        itemName: '',
        itemDesc: '',
        itemLink: '',
        itemPrice: null,
        isCompleted: false,
      })
      showToast(t('messages.createSuccess', language) || '생성되었습니다.', 'success')
      setTimeout(() => onItemUpdated?.(), 0)
    } catch (error: any) {
      // 에러 시 스피너를 먼저 끄고 업그레이드 모달 표시 (모달이 뜬 위에서 스피너가 돌지 않도록)
      setIsCreatingItem(false)
      const handled = await handleRestrictionError(error)
      if (!handled) {
        showToast(t('messages.itemCreateFailed', language) || '아이템 생성에 실패했습니다.', 'error')
      }
    } finally {
      setIsCreatingItem(false)
      stopLoading()
    }
  }

  const handleEditItem = async (item: DreamityItemResDto) => {
    // 먼저 서버에서 최신 상세를 가져오고 모달을 연다. 단일 조회 중에는 전체 스켈레톤 숨김
    isFetchingItemForEditRef.current = true
    try {
      let fetched = null
      // try multiple sources for dreamId: route param, selectedDream from context, or URL path
      let dreamIdToUse = currentDreamId ?? selectedDream?.dreamId ?? item.dreamId ?? null
      if (!dreamIdToUse) {
        const m =
          typeof window !== 'undefined' ? window.location.pathname.match(/\/dreams\/([^/]+)/) : null
        if (m?.[1]) dreamIdToUse = m[1]
      }

      if (dreamIdToUse) {
        fetched = await fetchItemById(dreamIdToUse, item.itemId)
      } else {
        // cannot call API without dreamId (new API requires dreamId). fallback to local item.
        fetched = null
      }
      if (fetched) {
        setEditForm({ ...fetched })
      } else {
        // fallback to local item if fetch failed
        setEditForm({ ...item })
      }
      setIsEditModalOpen(true)
    } catch (err) {
      console.error('fetchItemById failed in handleEditItem', err)
      setEditForm({ ...item })
      setIsEditModalOpen(true)
    } finally {
      isFetchingItemForEditRef.current = false
    }
  }

  const handleUpdateItem = async () => {
    if (!editForm) {
      console.error('editForm이 null입니다.')
      showToast(t('messages.noItemDataToEdit', language), 'error')
      return
    }

    if (!editForm.itemId) {
      console.error('itemId가 없습니다:', editForm)
      showToast(t('messages.noItemId', language), 'error')
      return
    }

    // 폼 검증
    if (!editForm.itemName?.trim()) {
      showToast(t('checklist.itemNameRequired', language) || '아이템 이름을 입력해주세요.', 'error')
      return
    }

    if (editForm.itemPrice !== null && editForm.itemPrice !== undefined && editForm.itemPrice < 0) {
      showToast(t('messages.invalidBudget', language) || '가격은 0 이상이어야 합니다.', 'error')
      return
    }

    try {
      setIsUpdatingItem(true)
      startLoading()
      const updateData = {
        itemName: editForm.itemName.trim(),
        itemDesc: editForm.itemDesc?.trim() || '',
        itemLink: editForm.itemLink?.trim() || '',
        itemPrice: editForm.itemPrice ?? 0,
        isCompleted: editForm.isCompleted ?? false,
      }
      
      if (!currentDreamId) {
        showToast(t('messages.noDreamId', language), 'error')
        return
      }
      await updateItem(currentDreamId, editForm.itemId, updateData)
      setIsEditModalOpen(false)
      setEditForm(null)
      showToast(t('messages.updateSuccess', language) || '수정되었습니다.', 'success')
      setTimeout(() => onItemUpdated?.(), 0)
    } catch (error) {
      console.error('아이템 수정 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'
      showToast(
        errorMessage || t('messages.itemUpdateFailed', language),
        'error'
      )
    } finally {
      setIsUpdatingItem(false)
      stopLoading()
    }
  }

  const handleToggleComplete = async (item: DreamityItemResDto) => {
    try {
      // 체크박스 클릭 시에만 isCompleted만 변경
      if (!currentDreamId) {
        showToast(t('messages.noDreamId', language), 'error')
        return
      }
      await updateItem(currentDreamId, item.itemId, {
        itemName: item.itemName,
        itemDesc: item.itemDesc,
        itemLink: item.itemLink,
        itemPrice: item.itemPrice,
        isCompleted: !item.isCompleted,
      })
    } catch (error) {
      showToast(t('messages.statusChangeFailed', language) || '상태 변경에 실패했습니다.', 'error')
    }
  }

  const handleDeleteItem = async () => {
    if (!itemToDelete || !currentPlanId) return
    try {
      setIsDeletingItem(true)
      startLoading()
      // call deleteItem with dreamId + itemId + planId to match new context API
      await deleteItem(currentDreamId!, itemToDelete.itemId, currentPlanId!)
      setIsDeleteModalOpen(false)
      setItemToDelete(null)
      showToast(t('messages.deleteSuccess', language) || '삭제되었습니다.', 'success')
      setTimeout(() => onItemUpdated?.(), 0)
    } catch (error) {
      showToast(t('messages.itemDeleteFailed', language) || '아이템 삭제에 실패했습니다.', 'error')
    } finally {
      setIsDeletingItem(false)
      stopLoading()
    }
  }

  const handleDeleteClick = (item: DreamityItemResDto) => {
    setItemToDelete(item)
    setIsDeleteModalOpen(true)
  }

  /* 목록 전체 로딩일 때만 스켈레톤. 아이템 수정용 단일 조회(fetchItemById) 중에는 스켈레톤 숨겨 수정 모달만 보이게 함 */
  if (itemLoading && !isFetchingItemForEditRef.current) {
    return (
      <div className="checklist">
        <div className="checklist__header">
          <Skeleton height={32} width={80} />
          <Skeleton height={32} width={200} />
          <Skeleton height={40} width={120} />
        </div>
        <div className="checklist__content">
          <div className="checklist__items">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="checklist__item checklist__item--skeleton">
                <SkeletonListItem imageSize={56} lines={2} style={{ minHeight: 72 }} />
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="checklist">
      {!isModal && (
        <PageHeader showBack={false} onBack={() => navigate(-1)} />
      )}

      {isModal && onClose && (
        <div className="checklist__modal-header modal__header">
          <h3 className="checklist__modal-title modal__header-title">{language === 'ko' ? '아이템' : 'Item'}</h3>
          <div className="checklist__modal-header-actions">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              aria-label={t('checklist.addItem', language)}
              title={t('checklist.addItem', language)}
              className="checklist__modal-add-item-btn modal__header-action"
            >
              <IconPlus size={20} />
            </Button>
            <button type="button" className="modal__close" onClick={onClose} aria-label={t('common.close', language)}>
              <IconClose size={20} />
            </button>
          </div>
        </div>
      )}
      <div className="checklist__content">
        {items.length === 0 ? (
          <EmptyState
            icon={<IconPackage size={32} />}
            title={t('checklist.noItems', language)}
            description={t('checklist.noItemsDesc', language) || '준비할 아이템을 추가해보세요!'}
          />
        ) : (
          <div className="checklist__items">
            {items.map((item) => (
              <Card
                key={item.itemId}
                className={`checklist__item ${item.isCompleted ? 'completed' : ''}`}
              >
                <div className="checklist__item-header">
                  <input
                    type="checkbox"
                    checked={item.isCompleted}
                    onChange={() => handleToggleComplete(item)}
                    onClick={(e) => e.stopPropagation()}
                    className="checklist__checkbox"
                  />
                  <h4 className="checklist__item-name">{item.itemName}</h4>
                  <div className="checklist__item-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditItem(item)
                      }}
                      title={t('common.edit', language)}
                      aria-label={t('common.edit', language)}
                      className="!min-w-9 !h-9 !p-1.5 !rounded-lg !border !border-[var(--color-card-border)] !bg-[var(--color-card)] !text-[var(--color-text-primary)] inline-flex items-center justify-center hover:!bg-slate-100"
                    >
                      <IconEdit size={18} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(item)
                      }}
                      title={t('common.delete', language)}
                      aria-label={t('common.delete', language)}
                      className="!min-w-9 !h-9 !p-1.5 !rounded-lg !border !border-red-200 !bg-[var(--color-card)] !text-red-600 inline-flex items-center justify-center hover:!bg-red-50"
                    >
                      <IconTrash size={18} />
                    </Button>
                  </div>
                </div>
                {item.itemDesc && (
                  <p className="checklist__item-desc">{item.itemDesc}</p>
                )}
                {(item.itemPrice !== null && item.itemPrice !== undefined) && (
                  <p className="checklist__item-price">
                    {t('dreams.itemBudget', language)}: {formatCurrency(item.itemPrice, language)}
                  </p>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 아이템 생성 모달 */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isCreatingItem && setIsCreateModalOpen(false)}
        title={t('checklist.addItemTitle', language)}
        size="md"
        showBackdrop
      >
        <div className={`checklist__modal-content ${isCreatingItem ? 'checklist__modal-content--loading' : ''}`}>
          <Input
            label={t('checklist.itemName', language)}
            value={createForm.itemName}
            onChange={(e) =>
              setCreateForm({ ...createForm, itemName: e.target.value })
            }
            placeholder={t('checklist.itemNamePlaceholder', language)}
            required
          />
          <Input
            label={t('checklist.itemDesc', language)}
            value={createForm.itemDesc}
            onChange={(e) =>
              setCreateForm({ ...createForm, itemDesc: e.target.value })
            }
            placeholder={t('checklist.itemDescPlaceholder', language)}
          />
          <Input
            label={t('dreams.budgetLabel', language)}
            type="number"
            min="0"
            value={createForm.itemPrice == null ? '' : String(createForm.itemPrice)}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || /^\d+$/.test(value)) {
                setCreateForm({
                  ...createForm,
                  itemPrice: value === '' ? null : parseInt(value, 10),
                })
              }
            }}
          />
          <div className="checklist__modal-actions">
            <Button onClick={handleCreateItem} disabled={isCreatingItem}>
              {isCreatingItem ? (
                <>
                  <span className="checklist__loading-spinner"></span>
                  {t('messages.processing', language) || '처리중입니다...'}
                </>
              ) : (
                t('common.create', language)
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 아이템 수정 모달 */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isUpdatingItem) {
            setIsEditModalOpen(false)
            setEditForm(null)
          }
        }}
        title={t('checklist.editItemTitle', language)}
        size="md"
        showBackdrop
      >
        <div className={`checklist__modal-content ${isUpdatingItem ? 'checklist__modal-content--loading' : ''}`}>
          {editForm && (
            <>
              <Input
                label={t('checklist.itemName', language)}
                value={editForm.itemName}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    itemName: e.target.value,
                  })
                }
                required
              />
              <Input
                label={t('checklist.itemDesc', language)}
                value={editForm.itemDesc}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    itemDesc: e.target.value,
                  })
                }
              />
              <Input
                label={t('dreams.budgetLabel', language)}
                type="number"
                min="0"
                value={editForm.itemPrice == null ? '' : String(editForm.itemPrice)}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '' || /^\d+$/.test(value)) {
                    setEditForm({
                      ...editForm,
                      itemPrice: value === '' ? null : parseInt(value, 10),
                    })
                  }
                }}
              />
              <div className="checklist__modal-actions">
                <Button onClick={handleUpdateItem} disabled={isUpdatingItem}>
                  {isUpdatingItem ? (
                    <>
                      <span className="checklist__loading-spinner"></span>
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('common.edit', language)
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingItem) {
            setIsDeleteModalOpen(false)
            setItemToDelete(null)
          }
        }}
        title={t('messages.deleteConfirmTitle', language)}
        size="sm"
        showBackdrop
        className="checklist__delete-modal"
      >
        <div className={`checklist__delete-confirm ${isDeletingItem ? 'checklist__modal-content--loading' : ''}`}>
          <div className="checklist__delete-confirm-icon">
            <IconTrash size={28} />
          </div>
          <p className="checklist__delete-confirm-message">
            {t('messages.deleteConfirm', language) || '정말로 삭제하시겠습니까?'}
          </p>
          <div className="checklist__delete-confirm-actions">
            <Button
              variant="ghost"
              onClick={() => {
                if (!isDeletingItem) {
                  setIsDeleteModalOpen(false)
                  setItemToDelete(null)
                }
              }}
              disabled={isDeletingItem}
            >
              {t('common.cancel', language) || '취소'}
            </Button>
            <Button variant="danger" onClick={handleDeleteItem} disabled={isDeletingItem}>
              {isDeletingItem ? (
                <>
                  <span className="checklist__loading-spinner"></span>
                  {t('messages.processing', language) || '처리중입니다...'}
                </>
              ) : (
                t('common.delete', language)
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Checklist
